import { Link } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import type { VerdictClarity, RecoPriority } from "./VerdictClarity";
import { verdictProseStyle } from "@/lib/verdict-typography";

/**
 * Verdict court "Hōjin" — bloc inline rendu EN FIN DE DÉBAT (sous la
 * discussion, au-dessus du bouton mémo et du cadre « Relancer le Conseil »).
 *
 * Pour les branches Business / Audit : indice de conviction + phrase + titres recos.
 * Pour la branche Tendances : classification + recommandation de l'instrument
 *   à la place de l'indice, puis phrase + titres recos.
 * Pour la branche Vérif : on n'utilise PAS ce composant (rendu dédié intact).
 *
 * DA : or #00E5FF sur fond noir #000, Playfair Display + JetBrains Mono.
 * Typo calibrée pour un bloc de pied de page (pas un écran plein).
 */

const HOJIN_GOLD = "#00E5FF";
const HOJIN_INK = "#000000";
const HOJIN_PAPER = "#F5EFE3";
const HOJIN_MUTED = "rgba(245,239,227,0.55)";

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'DM Mono', ui-monospace, monospace";

const PRIORITY_DOT: Record<RecoPriority, string> = {
  critical: "#7A2A2A",
  high: HOJIN_GOLD,
  medium: "#7A7A7A",
};

export interface TendancesInstrumentLite {
  classification: string; // "Micro" | "Saisonnière" | "Macro" | "Méga"
  reco: string; // "Acheter" | "Surveiller" | "Ignorer"
}

interface VerdictShortProps {
  conversationId: string;
  /** verdict_clarity structuré (peut être null → fallback). */
  clarity: VerdictClarity | null;
  /** Pour la branche Tendances : remplace l'indice par classification + reco. */
  instrument?: TendancesInstrumentLite | null;
  /** Fallback score 0–10 (utilisé si clarity null). */
  fallbackScore?: number | null;
  /** Fallback prose (utilisée si clarity null). */
  fallbackPhrase?: string | null;
  /** Affiche le CTA « Voir le mémo complet » sous le bloc (défaut: true). */
  showMemoButton?: boolean;
}

export function VerdictShort({
  conversationId,
  clarity,
  instrument,
  fallbackScore,
  fallbackPhrase,
  showMemoButton = true,
}: VerdictShortProps) {
  const score = clarity?.verdict?.score;
  const rawValue = typeof score?.value === "number" ? score.value : null;
  const value =
    rawValue !== null
      ? rawValue <= 1
        ? rawValue * 10
        : rawValue
      : typeof fallbackScore === "number"
        ? fallbackScore
        : null;

  const phrase = clarity?.verdict?.phrase ?? fallbackPhrase ?? null;
  const accent =
    score?.color_hex && /^#[0-9a-f]{6}$/i.test(score.color_hex)
      ? score.color_hex
      : HOJIN_GOLD;

  const recos = Array.isArray(clarity?.recommandations)
    ? clarity.recommandations
    : [];

  const isTendances = !!instrument;

  return (
    <section
      aria-label="Verdict du Conseil"
      style={{
        background: HOJIN_INK,
        color: HOJIN_PAPER,
        border: `1px solid ${HOJIN_GOLD}55`,
        padding: "40px 32px 36px",
        margin: "24px 0 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
      }}
    >
      {/* Eyebrow */}
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          letterSpacing: 4,
          textTransform: "uppercase",
          color: HOJIN_GOLD,
          marginBottom: 24,
        }}
      >
        — Verdict du Conseil —
      </div>

      {/* 1. Indice OU Classification/Recommandation (Tendances) */}
      {isTendances ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 28,
            gap: 10,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 14,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontFamily: FONT_SERIF,
                fontWeight: 400,
                fontSize: "clamp(28px, 4vw, 40px)",
                color: HOJIN_GOLD,
                letterSpacing: "-0.01em",
                lineHeight: 1,
              }}
            >
              {instrument!.classification}
            </span>
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 11,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: HOJIN_MUTED,
              }}
            >
              →
            </span>
            <span
              style={{
                fontFamily: FONT_MONO,
                fontWeight: 500,
                fontSize: "clamp(20px, 2.6vw, 26px)",
                color: HOJIN_PAPER,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
            >
              {instrument!.reco}
            </span>
          </div>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: HOJIN_GOLD,
            }}
          >
            Tendance · Recommandation
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 28,
          }}
        >
          {value !== null ? (
            <div
              style={{
                display: "inline-flex",
                alignItems: "baseline",
                gap: 8,
                lineHeight: 1,
              }}
            >
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontWeight: 300,
                  fontSize: "clamp(56px, 8vw, 88px)",
                  color: accent,
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                }}
              >
                {value.toFixed(1)}
              </span>
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontWeight: 300,
                  fontSize: "clamp(14px, 1.6vw, 18px)",
                  color: HOJIN_MUTED,
                  letterSpacing: "0.04em",
                }}
              >
                / 10
              </span>
            </div>
          ) : (
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: 12,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: HOJIN_MUTED,
                padding: "16px 0",
              }}
            >
              — indice indisponible —
            </div>
          )}
          <div
            style={{
              marginTop: 10,
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: HOJIN_GOLD,
            }}
          >
            Indice de conviction
          </div>
        </div>
      )}

      {/* 2. Phrase qui tranche */}
      {phrase ? (
        <p
          style={{
            ...verdictProseStyle,
            color: HOJIN_PAPER,
            margin: "0 auto 32px",
            maxWidth: 620,
            textAlign: "center",
          }}
        >
          « {phrase} »
        </p>
      ) : null}


      {/* 3. Titres des recommandations */}
      {recos.length > 0 ? (
        <ul
          aria-label="Recommandations"
          style={{
            listStyle: "none",
            padding: 0,
            margin: "0 auto",
            maxWidth: 620,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            borderTop: `0.5px solid ${HOJIN_GOLD}33`,
          }}
        >
          {recos.map((r, i) => (
            <li
              key={r.id || i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "12px 6px",
                borderBottom: `0.5px solid ${HOJIN_GOLD}33`,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: PRIORITY_DOT[r.priority] ?? HOJIN_GOLD,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 10,
                  letterSpacing: 2,
                  color: HOJIN_MUTED,
                  flexShrink: 0,
                  width: 22,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                style={{
                  fontFamily: FONT_SERIF,
                  fontSize: "clamp(14px, 1.5vw, 16px)",
                  fontWeight: 400,
                  color: HOJIN_PAPER,
                  lineHeight: 1.4,
                  letterSpacing: "-0.005em",
                }}
              >
                {r.titre}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* 4. Bouton — Voir le mémo complet */}
      {showMemoButton ? (
        <Link
          to="/memo/$id"
          params={{ id: conversationId }}
          style={{
            marginTop: 32,
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            background: HOJIN_GOLD,
            color: HOJIN_INK,
            padding: "14px 32px",
            fontFamily: FONT_MONO,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 3,
            textTransform: "uppercase",
            textDecoration: "none",
            border: `1px solid ${HOJIN_GOLD}`,
            transition: "background .2s ease, color .2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = HOJIN_GOLD;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = HOJIN_GOLD;
            e.currentTarget.style.color = HOJIN_INK;
          }}
        >
          <FileText size={14} />
          Voir le mémo complet
        </Link>
      ) : null}
    </section>
  );
}
